import {useMemo} from 'react'
import useAuth from './useAuth'
import {useDeviceContext} from './useDevice'
import {AuthService, AuthState} from '../index'

export type CombinedAuthState = AuthState & {
    isDeviceAuthenticated: boolean
    deviceUserInfo: {
        id?: string
        email?: string
        name?: string
        given_name?: string
        family_name?: string
    } | null
}

/**
 * Combined authentication hook that considers both OAuth and device authentication.
 *
 * A user is considered "authenticated" if:
 * 1. They have valid OAuth tokens (traditional email login), OR
 * 2. They have an authenticated device with userInfo
 *
 * Usage:
 *   const {authState, authService} = useCombinedAuth()
 *   if (authState.isAuthenticated) { ... }
 *
 * The returned authState.isAuthenticated will be true for either auth method.
 * Use authState.isDeviceAuthenticated to specifically check device auth.
 */
export default function useCombinedAuth(): {
    authService: AuthService
    authState: CombinedAuthState
} {
    const {authService, authState: oauthState} = useAuth()
    const device = useDeviceContext()

    // Device is considered authenticated if it has userInfo with an id
    const isDeviceAuthenticated = !!(device.userInfo?.id)

    const authState = useMemo<CombinedAuthState>(() => {
        // Combined authentication: either OAuth or device auth
        const isAuthenticated = oauthState.isAuthenticated || isDeviceAuthenticated
        // Stay "pending" through three windows: (1) useDevice still
        // initializing; (2) OAuth still resolving; (3) we have a
        // persisted device (seedKid set) but the initial userInfo
        // fetch hasn't settled yet — without (3), guards like
        // update_contact_info's "redirect to login if !auth &&
        // !pending" fire in the gap between setIsReady(true) and
        // wasmGetUserInfo() resolving, producing a redirect loop
        // every fresh page load post-signup.
        //
        // Window (3) closes when userInfoSettled flips — including the
        // case where userInfo never arrives because the server does not
        // know this device (an abandoned signup leaves a locally-saved,
        // never-registered device). seedKid and userInfoSettled must
        // therefore be memo deps: without them this memo kept serving
        // isPending=true after settle, every isPending-gated page sat on
        // its spinner, and the CCAF link never reached login() (the
        // "keeps spinning" report; tests/e2e/forms-ccaf-orphan-device).
        const isPending = !(device.isReady)
            || oauthState.isPending
            || (device.seedKid !== null && !device.userInfoSettled)
        // Merge userinfo: prefer OAuth userinfo, fall back to device userInfo
        const mergedUserinfo = oauthState.isAuthenticated
            ? oauthState.userinfo
            : isDeviceAuthenticated
                ? {
                    email: device.userInfo?.email || '',
                    givenName: device.userInfo?.given_name || '',
                    familyName: device.userInfo?.family_name || '',
                    name: device.userInfo?.name || '',
                }
                : oauthState.userinfo

        return {
            ...oauthState,
            isAuthenticated,
            isPending,
            userinfo: mergedUserinfo,
            isDeviceAuthenticated,
            deviceUserInfo: device.userInfo,
        }
    }, [device.isReady, device.seedKid, device.userInfoSettled, oauthState, isDeviceAuthenticated, device.userInfo])

    return {
        authService,
        authState,
    }
}
